"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// 导入现有的类型定义
import { DownloadInfoImgItem } from '@/app/download/components/DownloadFirstScreen/constants';

export interface DownloadResource {
  platform: string;
  url: string;
  text: string;
  overlayTitle: string;
  icon: string;
  version: string;
}

export interface DownloadInfoType {
  type: string;
  title: string;
  hint: string;
  isEnabled: boolean;
  icon: string;
  resource: DownloadResource[];
}

interface DownloadContextType {
  downloadInfo: DownloadInfoType[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getMacArmResource: () => DownloadResource | undefined;
  getMacIntelResource: () => DownloadResource | undefined;
  getWindowsResource: () => DownloadResource | undefined;
}

const DownloadContext = createContext<DownloadContextType | undefined>(undefined);

export const useDownloadContext = () => {
  const context = useContext(DownloadContext);
  if (context === undefined) {
    throw new Error('useDownloadContext must be used within a DownloadProvider');
  }
  return context;
};

interface DownloadProviderProps {
  children: ReactNode;
}

export const DownloadProvider: React.FC<DownloadProviderProps> = ({ children }) => {
  const [downloadInfo, setDownloadInfo] = useState<DownloadInfoType[]>(DownloadInfoImgItem);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDownloadInfo = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch("/api/download");
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && Array.isArray(data.list) && data.list.length > 0) {
        console.log('Download info updated from API:', data.list);
        setDownloadInfo(data.list);
      } else {
        console.log("API returned empty or invalid data, keeping default");
        // Keep using default download info
      }
    } catch (error) {
      console.error("Error fetching download list:", error);
      setError(error instanceof Error ? error.message : 'Failed to fetch download info');
      // Keep using current download info, don't reset to default
    } finally {
      setIsLoading(false);
    }
  };

  // 预加载下载信息
  useEffect(() => {
    fetchDownloadInfo();
  }, []);

  // 便捷方法获取特定平台的下载资源
  const getMacArmResource = (): DownloadResource | undefined => {
    const macInfo = downloadInfo.find(item => item.type === "mac");
    return macInfo?.resource.find(r => r.platform === "mac_arm");
  };

  const getMacIntelResource = (): DownloadResource | undefined => {
    const macInfo = downloadInfo.find(item => item.type === "mac");
    return macInfo?.resource.find(r => r.platform === "mac_intel");
  };

  const getWindowsResource = (): DownloadResource | undefined => {
    const windowsInfo = downloadInfo.find(item => item.type === "windows");
    return windowsInfo?.resource.find(r => r.platform === "windows");
  };

  const value: DownloadContextType = {
    downloadInfo,
    isLoading,
    error,
    refetch: fetchDownloadInfo,
    getMacArmResource,
    getMacIntelResource,
    getWindowsResource,
  };

  return (
    <DownloadContext.Provider value={value}>
      {children}
    </DownloadContext.Provider>
  );
};